import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import apiClient from "../utils/apiClient";
import { getCaptchaErrorMessage } from "../utils/captchaErrors";
import TurnstileWidget from "./TurnstileWidget";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  InputLabel,
  TextField,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { setUser } from "../features/user/userSlice";
import { loadSettings } from "../features/app/appSlice";
import Role from "../enum/Role.enum";
import { useTranslation } from "react-i18next";

const AuthContainer = styled.div`
  width: 332px;
  position: relative;
  background-color: white;
  padding: 16px;
  border-radius: 8px;
`;

const BGreen = styled.div`
  color: #2d7c2d;
  padding-top: 6px;
  font-size: 12px;
  cursor: pointer;
`;

const InputLabelStyled = styled(InputLabel)`
  &.MuiInputLabel-root {
    font-size: 12px;
    padding-bottom: 8px;
    color: black;
  }
`;

const VerificationTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 12px;
`;

const CAPTCHA_STATUS = Object.freeze({
  LOADING: "loading",
  ENABLED: "enabled",
  DISABLED: "disabled",
  ERROR: "error",
});

const AuthForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // ----- Верификация Turnstile -----
  const [captchaStatus, setCaptchaStatus] = useState(CAPTCHA_STATUS.LOADING);
  const [captchaSiteKey, setCaptchaSiteKey] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [authStep, setAuthStep] = useState("login");
  const [pendingAction, setPendingAction] = useState(null);

  // ----- Для логина -----
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [isForgotPasswordFlow, setIsForgotPasswordFlow] = useState(false);
  const [step, setStep] = useState(1);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotCode, setForgotCode] = useState("");
  const [passNew, setPassNew] = useState("");
  const [passNewTwo, setPassNewTwo] = useState("");
  const [isResetRequestCompleted, setIsResetRequestCompleted] = useState(false);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken("");
    setTurnstileKey((current) => current + 1);
  }, []);

  const loadCaptchaConfig = useCallback(async () => {
    try {
      const response = await apiClient.get("/auth/captcha-config");
      const { enabled, siteKey } = response.data;
      setCaptchaSiteKey(siteKey || "");
      setCaptchaStatus(
        enabled ? CAPTCHA_STATUS.ENABLED : CAPTCHA_STATUS.DISABLED,
      );
    } catch (err) {
      setCaptchaStatus(CAPTCHA_STATUS.ERROR);
    }
  }, []);

  useEffect(() => {
    loadCaptchaConfig();
  }, [loadCaptchaConfig]);

  const handleTurnstileSuccess = useCallback((token) => {
    setTurnstileToken(token);
    setError("");
  }, []);

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken("");
    setError(getCaptchaErrorMessage(null, "captcha_invalid"));
  }, []);

  const handleTurnstileExpired = useCallback(() => {
    setTurnstileToken("");
    setError(getCaptchaErrorMessage(null, "captcha_expired"));
    resetTurnstile();
  }, [resetTurnstile]);

  const handleLogin = async () => {
    setError("");
    try {
      setIsLoading(true);
      const response = await apiClient.post("/auth", {
        email,
        password,
      });

      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));

      dispatch(setUser(response.data.user));
      dispatch(loadSettings());

      setTimeout(() => {
        if (response.data.user.privileges.includes(Role.RESET_PASSWORD)) {
          navigate("/change_password");
        } else {
          navigate("/panel");
        }
      }, 100);
    } catch (err) {
      setError(err?.response?.data?.error ?? t("auth.wrongCredentials"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginSubmit = () => {
    setError("");

    if (captchaStatus === CAPTCHA_STATUS.ENABLED) {
      if (!email || !password) {
        setError(t("auth.enterEmailAndPassword"));
        return;
      }
      setPendingAction("login");
      setAuthStep("captcha");
      return;
    }

    handleLogin();
  };

  const handleVerifyAndContinue = async () => {
    setError("");

    if (!turnstileToken) {
      setError(getCaptchaErrorMessage(null, "captcha_required"));
      return;
    }

    try {
      setIsLoading(true);
      const verifyResponse = await apiClient.post("/auth/verify", {
        turnstileToken,
      });
      const verificationToken = verifyResponse.data.verificationToken;

      if (pendingAction === "forgotSendCode") {
        const response = await apiClient.post("/agents/changepasswordna", {
          email: forgotEmail,
          verificationToken,
        });

        setAuthStep("login");
        setPendingAction(null);
        resetTurnstile();

        if (response.data.error) {
          setError(`${t("auth.error")}: ${response.data.error}`);
        } else {
          alert(t("auth.codeSent"));
          setIsResetRequestCompleted(true);
        }
        return;
      }

      const response = await apiClient.post("/auth", {
        email,
        password,
        verificationToken,
      });

      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));

      dispatch(setUser(response.data.user));
      dispatch(loadSettings());

      setTimeout(() => {
        if (response.data.user.privileges.includes(Role.RESET_PASSWORD)) {
          navigate("/change_password");
        } else {
          navigate("/panel");
        }
      }, 100);
    } catch (err) {
      const errorCode = err?.response?.data?.error_code;
      if (
        errorCode?.startsWith("verification_") ||
        errorCode?.startsWith("captcha_")
      ) {
        resetTurnstile();
        setError(getCaptchaErrorMessage(err?.response?.data?.error, errorCode));
      } else if (pendingAction === "forgotSendCode") {
        setAuthStep("login");
        setPendingAction(null);
        resetTurnstile();
        setError(err?.response?.data?.error ?? t("auth.sendCodeError"));
      } else {
        setAuthStep("login");
        setPendingAction(null);
        resetTurnstile();
        setError(err?.response?.data?.error ?? t("auth.wrongCredentials"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordSendCode = async () => {
    setError("");

    if (captchaStatus === CAPTCHA_STATUS.ENABLED) {
      setPendingAction("forgotSendCode");
      setAuthStep("captcha");
      return;
    }

    try {
      setIsLoading(true);
      const response = await apiClient.post("/agents/changepasswordna", {
        email: forgotEmail,
      });
      if (response.data.error) {
        setError(`${t("auth.error")}: ${response.data.error}`);
      } else {
        alert(t("auth.codeSent"));
        setIsResetRequestCompleted(true);
      }
    } catch (err) {
      setError(
        getCaptchaErrorMessage(
          err?.response?.data?.error,
          err?.response?.data?.error_code,
        ) ?? t("auth.sendCodeError"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordChange = async () => {
    setError("");
    try {
      setIsLoading(true);
      const response = await apiClient.post("/agents/changepasswordna", {
        email: forgotEmail,
        code: forgotCode,
        pass_new: passNew,
        pass_new_two: passNewTwo,
      });

      if (response.data.error) {
        setError(`${t("auth.error")}: ${response.data.error_type}`);
      } else {
        alert(t("auth.passwordChanged"));
        setIsForgotPasswordFlow(false);
        setStep(1);
      }
    } catch (err) {
      const responseError = err?.response?.data;
      setError(
        responseError?.message ??
          (typeof responseError?.error === "string"
            ? responseError.error
            : t("auth.changePasswordError")),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const goToForgotPassword = () => {
    setIsForgotPasswordFlow(true);
    setStep(1);
    setError("");
    setForgotEmail("");
    setForgotCode("");
    setPassNew("");
    setPassNewTwo("");
    setIsResetRequestCompleted(false);
  };

  const goToLogin = () => {
    setIsForgotPasswordFlow(false);
    setError("");
  };

  const goBackToLogin = () => {
    setAuthStep("login");
    setPendingAction(null);
    resetTurnstile();
    setError("");
  };

  const showCaptchaStep =
    captchaStatus === CAPTCHA_STATUS.ENABLED && authStep === "captcha";
  const showLoginForm =
    !isForgotPasswordFlow &&
    captchaStatus !== CAPTCHA_STATUS.LOADING &&
    captchaStatus !== CAPTCHA_STATUS.ERROR &&
    (captchaStatus === CAPTCHA_STATUS.DISABLED || authStep === "login");

  return (
    <AuthContainer>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          position: "absolute",
          borderRadius: 2,
        }}
        open={
          isLoading || captchaLoading || captchaStatus === CAPTCHA_STATUS.LOADING
        }
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {captchaStatus === CAPTCHA_STATUS.ERROR && (
        <Alert severity="error" sx={{ fontSize: "12px" }}>
          {getCaptchaErrorMessage(null, "captcha_service_unavailable")}
        </Alert>
      )}

      {showCaptchaStep && (
        <Box>
          <VerificationTitle>
            {t("auth.securityVerification")}
          </VerificationTitle>
          <TurnstileWidget
            key={turnstileKey}
            siteKey={captchaSiteKey}
            onSuccess={handleTurnstileSuccess}
            onError={handleTurnstileError}
            onExpired={handleTurnstileExpired}
            onLoadingChange={setCaptchaLoading}
          />
          <Button
            onClick={handleVerifyAndContinue}
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            sx={{ mt: 2 }}
            disabled={isLoading || captchaLoading || !turnstileToken}
          >
            {t("auth.continue")}
          </Button>
          <BGreen onClick={goBackToLogin} style={{ marginTop: "10px" }}>
            {t("auth.back")}
          </BGreen>
          {error && (
            <Alert severity="error" sx={{ fontSize: "12px", my: 1 }}>
              {error}
            </Alert>
          )}
        </Box>
      )}

      {showLoginForm && (
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleLoginSubmit();
          }}
        >
          <Box>
            <InputLabelStyled htmlFor="auth-email">
              {t("auth.email")}
            </InputLabelStyled>
            <TextField
              id="auth-email"
              variant="outlined"
              size="small"
              fullWidth
              placeholder="email@examle.com"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <InputLabelStyled htmlFor="auth-password">
              {t("auth.password")}
            </InputLabelStyled>
            <TextField
              id="auth-password"
              variant="outlined"
              size="small"
              fullWidth
              placeholder="********"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
          </Box>

          <BGreen onClick={goToForgotPassword}>
            {t("auth.forgotPassword")}
          </BGreen>
          <Button
            type="button"
            onClick={handleLoginSubmit}
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            sx={{ mt: 2 }}
            disabled={isLoading}
          >
            {t("auth.login")}
          </Button>
          {error && (
            <Alert severity="error" sx={{ fontSize: "12px", my: 1 }}>
              {error}
            </Alert>
          )}
        </Box>
      )}

      {isForgotPasswordFlow && !showCaptchaStep && (
        <>
          {step === 1 && (
            <Box component="form">
              <Box>
                <InputLabelStyled htmlFor="auth-forgotEmail">
                  {t("auth.forgotPasswordTitle")}
                </InputLabelStyled>
                <TextField
                  id="auth-forgotEmail"
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="email@examle.com"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  disabled={isLoading}
                />
              </Box>

              {isResetRequestCompleted ? (
                <Button
                  onClick={() => setStep(2)}
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  disabled={isLoading}
                  sx={{ mt: 2 }}
                >
                  {t("auth.haveCode")}
                </Button>
              ) : (
                <Button
                  onClick={handleForgotPasswordSendCode}
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  disabled={isLoading}
                  sx={{ mt: 2 }}
                >
                  {t("auth.sendCode")}
                </Button>
              )}

              <BGreen onClick={goToLogin} style={{ marginTop: "10px" }}>
                {t("auth.backToLogin")}
              </BGreen>
            </Box>
          )}

          {step === 2 && (
            <Box component="form" autoComplete="off">
              <Box>
                <InputLabelStyled htmlFor="auth-forgotCode">
                  {t("auth.codeFromEmail")}
                </InputLabelStyled>
                <TextField
                  id="auth-forgotCode"
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder={t("auth.codePlaceholder")}
                  value={forgotCode}
                  onChange={(e) => setForgotCode(e.target.value)}
                  disabled={isLoading}
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <InputLabelStyled htmlFor="auth-passNew">
                  {t("auth.newPassword")}
                </InputLabelStyled>
                <TextField
                  id="auth-passNew"
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder={t("auth.newPassword")}
                  type="password"
                  value={passNew}
                  onChange={(e) => setPassNew(e.target.value)}
                  disabled={isLoading}
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <InputLabelStyled htmlFor="auth-passNewTwo">
                  {t("auth.repeatNewPassword")}
                </InputLabelStyled>
                <TextField
                  id="auth-passNewTwo"
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder={t("auth.repeatNewPasswordPlaceholder")}
                  type="password"
                  value={passNewTwo}
                  onChange={(e) => setPassNewTwo(e.target.value)}
                  disabled={isLoading}
                />
              </Box>

              <Button
                onClick={handleForgotPasswordChange}
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={isLoading}
                sx={{ mt: 2 }}
              >
                {t("auth.changePassword")}
              </Button>

              <BGreen onClick={goToLogin} style={{ marginTop: "10px" }}>
                {t("auth.backToLogin")}
              </BGreen>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ fontSize: "12px", my: 1 }}>
              {error}
            </Alert>
          )}
        </>
      )}
    </AuthContainer>
  );
};

export default AuthForm;
